// Error Boundary Component - Catches React errors and displays fallback UI
import type { ErrorInfo, ReactNode } from 'react';

import { Component } from 'react';

import i18n from '@/i18n';
import { getLogger } from '@/lib/logger/logger';

const logger = getLogger('ErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to an error reporting service
    logger.error('ErrorBoundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Store error info in state for display
    this.setState({
      errorInfo,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // In production, you might want to send this to Sentry or similar service
    if (import.meta.env.PROD) {
      // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className='min-h-screen flex items-center justify-center bg-gray-50 px-4'>
          <div className='max-w-md w-full bg-white shadow-lg rounded-lg p-8'>
            <div className='flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4'>
              <svg className='w-6 h-6 text-red-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                />
              </svg>
            </div>

            <h1 className='text-2xl font-bold text-gray-900 text-center mb-2'>
              {i18n.t('common:errorBoundary.title')}
            </h1>

            <p className='text-gray-600 text-center mb-6'>{i18n.t('common:errorBoundary.description')}</p>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details className='mb-6 p-4 bg-gray-100 rounded-md text-sm'>
                <summary className='cursor-pointer font-semibold text-gray-700 mb-2'>
                  {i18n.t('common:errorBoundary.errorDetails')}
                </summary>
                <div className='mt-2'>
                  <p className='font-mono text-red-600 mb-2'>{this.state.error.message}</p>
                  {this.state.error.stack && (
                    <pre className='text-xs overflow-auto bg-gray-900 text-gray-100 p-2 rounded'>
                      {this.state.error.stack}
                    </pre>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <details className='mt-2'>
                      <summary className='cursor-pointer font-semibold text-gray-700'>
                        {i18n.t('common:errorBoundary.componentStack')}
                      </summary>
                      <pre className='text-xs overflow-auto bg-gray-900 text-gray-100 p-2 rounded mt-1'>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              </details>
            )}

            <div className='flex gap-3'>
              <button
                onClick={this.handleReset}
                className='flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium'
              >
                {i18n.t('common:errorBoundary.tryAgain')}
              </button>
              <button
                onClick={this.handleReload}
                className='flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium'
              >
                {i18n.t('common:errorBoundary.reloadPage')}
              </button>
            </div>

            <p className='text-xs text-gray-500 text-center mt-4'>
              {i18n.t('common:errorBoundary.contactSupport')}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
